import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { round } from 'utils';
import { previousModalPage } from 'actions/modalPage';
import { addProduct } from 'actions/ShoppingCart';
import CloseButton from 'components/CustomButtons/CloseButton';
import SaveButton from 'components/CustomButtons/SaveButton';
// eslint-disable-next-line import/order
import { getAllWarehouses } from 'reducers/warehouses';
import { getShowPricesWithTax } from 'reducers/configs/settings';
import { getProducts } from 'services/ErplyAPI';

import './ProductView.scss';

import Loader from 'components/Loader';
import { PluginComponent } from 'plugins';

const ProductView = ({ productItem = null, title = null }) => {
  const [override, setOverride] = useState(null);
  const warehouses = useSelector(getAllWarehouses);
  const dispatch = useDispatch();
  useEffect(() => {
    setOverride(null);
    getProducts({
      productIDs: productItem.productID,
      from: 0,
      count: 1,
      getStockInfo: 1,
    }).then(res => setOverride(res.records[0]));
  }, [productItem]);

  const shouldUsePriceWithVAT = useSelector(getShowPricesWithTax);

  const price = round(
    shouldUsePriceWithVAT
      ? productItem.priceListPriceWithVat ?? productItem.priceWithVat
      : productItem.priceListPrice ?? productItem.price,
    2,
  );

  /**
   * @param {arrayOf(object)} retailerWarehouses - Warehouses that the retailer posses (getWarehouses API response)
   * @param {object} productInWarehouse - warehouses property of a product object (state.products.singleProduct.warehouses)
   *
   */
  const { t } = useTranslation('product');
  const displayStockByWarehouse = (retailerWarehouses, productInWarehouse) => {
    return retailerWarehouses.map(warehouse => {
      const stock = productInWarehouse?.[`${warehouse.warehouseID}`];
      const asNumber = a => (a !== undefined ? Number(a) : '?');
      return (
        <tr key={`stockIn${warehouse.name}`}>
          <td>{warehouse.name}</td>
          <td>{asNumber(stock?.totalInStock)}</td>
          <td>{asNumber(stock?.reserved)}</td>
          <td>{asNumber(stock?.free)}</td>
        </tr>
      );
    });
  };

  return productItem ? (
    <div className="product-view">
      <Modal.Header>
        <Modal.Title>{title || t('productView.title')}</Modal.Title>
        <span style={{ flexGrow: 1 }} />
        <SaveButton
          title={t('common:add', { context: 'new' })}
          action={() =>
            dispatch(
              addProduct({
                productID: productItem.productID,
                amount: 1,
                manualDiscount: 0,
              }),
            )
          }
        />
        <CloseButton action={() => dispatch(previousModalPage())} />
      </Modal.Header>
      <Table>
        <tbody>
          <tr>
            <td>{t('relatedProducts.headers.name')}</td>
            <td>{productItem.name}</td>
          </tr>
          <tr>
            <td>{t('relatedProducts.headers.code')}</td>
            <td>{productItem.code}</td>
          </tr>
          <tr>
            <td>{t('relatedProducts.headers.ean')}</td>
            <td>{productItem.code2}</td>
          </tr>
          <tr>
            <td>{t('relatedProducts.headers.price')}</td>
            <td>{price}</td>
          </tr>
          <tr>
            <td>{t('relatedProducts.headers.group')}</td>
            <td>{productItem.groupName}</td>
          </tr>
          <tr>
            <td>{t('relatedProducts.headers.category')}</td>
            <td>{productItem.categoryName}</td>
          </tr>
        </tbody>
      </Table>
      <PluginComponent hookname="UIStockDataTable">
        <h4>{t('relatedProducts.headers.stockInfo.title')}</h4>
        <Loader show={!override} block>
          <Table>
            <thead>
              <tr>
                <th>{t('relatedProducts.headers.stockInfo.warehouse')}</th>
                <th>{t('relatedProducts.headers.stockInfo.total')}</th>
                <th>{t('relatedProducts.headers.stockInfo.reserved')}</th>
                <th>{t('relatedProducts.headers.stockInfo.free')}</th>
              </tr>
            </thead>
            <tbody>
              {displayStockByWarehouse(warehouses, override?.warehouses)}
            </tbody>
          </Table>
        </Loader>
      </PluginComponent>
    </div>
  ) : null;
};

ProductView.propTypes = {
  /** Goes to previous modal page on col2 */
  previousModalPage: PropTypes.func,
  /** Product that its information will be displayed in this view */
  productItem: PropTypes.object,
  /** Warehouses that the retailer has */
  warehouses: PropTypes.arrayOf(PropTypes.object),
  /** Add product to shopping cart reducer */
  addProduct: PropTypes.func,
};

export default ProductView;
